import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { pushChannelCopy } from "~/components/notifications/channel-copy";
import { SettingsPage } from "~/components/settings/SettingsPage";
import { useProfileSave } from "~/components/settings/use-profile-save";
import { isNative } from "~/lib/capacitor";
import {
  ensureNotificationPermission,
  notificationPermissionState,
} from "~/lib/push-notifications";
import { useAuthStore } from "~/store/auth-store";

type PermissionState = Awaited<ReturnType<typeof notificationPermissionState>>;

/**
 * What the member agrees to receive.
 *
 * Two different things share the screen because they answer one question for the
 * member: the e-mail consent, which the account owns, and the webinar push switch,
 * which the account owns *and* the phone can veto. The veto is stated rather than
 * hidden — a switch that reads "on" while iOS silently drops every notification is
 * the worst of the possible outcomes, and it is not recoverable from inside the app.
 *
 * The switches save the moment they are flipped — a toggle with a Save button beside
 * it invites the reading that the toggle alone did nothing. The store holds the truth,
 * so a failed write leaves the switch showing the value that is actually stored.
 */
export function NotificationsRoute() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { save, saving, status, dismiss } = useProfileSave();
  const [permission, setPermission] = useState<PermissionState | null>(null);

  useEffect(() => {
    let cancelled = false;
    void notificationPermissionState().then((state) => {
      if (!cancelled) setPermission(state);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!user) return null;

  const toggleEventPush = async (enabled: boolean) => {
    await save({ event_push_enabled: enabled });
    if (!enabled) return;
    // Switching the preference on is also the moment to ask the OS: the member has
    // just said yes to the thing the dialog is about. Failing to get permission must
    // not undo the preference — the server switch is what the next device honours.
    try {
      await ensureNotificationPermission({
        userId: user.id,
        channels: pushChannelCopy(t),
      });
    } catch {
      // Nothing to show: the status line below re-reads the real state either way.
    }
    setPermission(await notificationPermissionState());
  };

  // Held as a key and resolved at render, so switching language re-translates it.
  const noticeKey = !isNative()
    ? "profile.notifications.permissionWeb"
    : permission === "denied"
      ? "profile.notifications.permissionDenied"
      : null;

  const rowSx = {
    // `labelPlacement="start"` ships negative margins that would pull the text off
    // the page gutter; the row owns its own alignment instead.
    marginInline: 0,
    gap: 2,
    justifyContent: "space-between",
  } as const;

  return (
    <SettingsPage
      title={t("profile.notifications.title")}
      status={status}
      onDismiss={dismiss}
    >
      {/*
        Label left, switch right — the shape of a settings row everywhere else in the
        app, and the one that keeps the label and its help text on the same left edge as
        the heading above them. No test id: MUI gives the input `role="switch"`, and a
        semantic locator is both stronger and avoids fighting the typed slot props.
      */}
      <FormControlLabel
        labelPlacement="start"
        // The label is a two-line block, not a string; without this MUI would wrap it in
        // a `<span>` and put a `<div>` inside an inline element.
        disableTypography
        control={
          <Switch
            // MUI pads the switch by 12px so the ripple clears the thumb, and `edge`
            // only pulls back 8 of them — the track would stop 4px short of the gutter
            // every other element on the screen lines up with.
            sx={{ marginRight: "-12px" }}
            checked={user.marketing_consent}
            disabled={saving}
            onChange={(event) =>
              void save({ marketing_consent: event.target.checked })
            }
          />
        }
        label={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="T2">
              {t("profile.notifications.marketing")}
            </Typography>
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("profile.notifications.marketingHelp")}
            </Typography>
          </Box>
        }
        sx={rowSx}
      />

      <FormControlLabel
        labelPlacement="start"
        disableTypography
        control={
          <Switch
            sx={{ marginRight: "-12px" }}
            checked={user.event_push_enabled}
            disabled={saving}
            onChange={(event) => void toggleEventPush(event.target.checked)}
          />
        }
        label={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="T2">
              {t("profile.notifications.eventPush")}
            </Typography>
            <Typography variant="T3" sx={{ color: "text.muted" }}>
              {t("profile.notifications.eventPushHelp")}
            </Typography>
          </Box>
        }
        sx={rowSx}
      />

      {noticeKey ? (
        <Alert severity="info" data-testid="profile.push-permission">
          {t(noticeKey)}
        </Alert>
      ) : null}
    </SettingsPage>
  );
}
